import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowRight, Check, Sparkles, WandSparkles } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, InlineAlert, MarkdownBody, Textarea, Tooltip, cn, diffWords, notifyDone, saveFailureText } from "@ncp/ui";
import { redraftText, type RedraftContext, type RedraftFormat, type RedraftResult } from "./api";

type Phase = { kind: "idle" } | { kind: "streaming"; text: string } | { kind: "done"; result: RedraftResult } | { kind: "error"; message: string };

const SUGGESTION_KEYS = ["formal", "shorter", "clearer", "grammar", "translateAr", "translateEn"] as const;

export interface RedraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The text as it stands when the dialog opens. */
  text: string;
  format: RedraftFormat;
  context?: RedraftContext;
  onApply: (text: string) => void;
}

/**
 * "Tell the assistant what needs changing." The officer writes an instruction — or picks a common one — and the redraft streams in
 * beside the current text; word-level changes can be highlighted before the officer applies it. Nothing is saved by the dialog itself:
 * applying only replaces the text in the editor, where the officer keeps full control and the usual record/approve step still follows.
 */
export function RedraftDialog({ open, onOpenChange, text, format, context, onApply }: RedraftDialogProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const [instruction, setInstruction] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [showChanges, setShowChanges] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // A fresh conversation each time the dialog opens; a redraft still streaming when it closes is cancelled.
  useEffect(() => {
    if (open) { setInstruction(""); setPhase({ kind: "idle" }); setShowChanges(false); }
    else abortRef.current?.abort();
  }, [open]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async () => {
    const trimmed = instruction.trim();
    if (trimmed.length < 3) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase({ kind: "streaming", text: "" });
    setShowChanges(false);
    try {
      const result = await redraftText(api, { text, instruction: trimmed, locale, format, context }, (soFar) => setPhase({ kind: "streaming", text: soFar }), controller.signal);
      if (controller.signal.aborted) return;
      setPhase({ kind: "done", result });
    } catch (e) {
      if (controller.signal.aborted) return;
      setPhase({ kind: "error", message: saveFailureText(e) });
    }
  };

  const proposed = phase.kind === "streaming" ? phase.text : phase.kind === "done" ? phase.result.text : "";
  const done = phase.kind === "done" ? phase.result : null;
  const unchanged = !!done && done.text.trim() === text.trim();
  const canApply = !!done && !done.offline && !unchanged && done.text.trim().length > 0;
  // Applying puts the text in the field; nothing is stored until the screen saves it.
  const apply = () => { if (!done) return; onApply(done.text); notifyDone(t("ai.redraft.applied")); onOpenChange(false); };
  const segments = useMemo(() => (showChanges && done ? diffWords(text, done.text) : null), [showChanges, done, text]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="max-h-[92vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WandSparkles className="size-4 text-seablue-700" /> {t("ai.redraft.title")}</DialogTitle>
          <DialogDescription>{t("ai.redraft.hint")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label htmlFor="redraft-instruction" className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("ai.redraft.instruction")}</label>
            <Textarea
              id="redraft-instruction" autoFocus rows={2} value={instruction} dir="auto" className="min-h-16"
              placeholder={t("ai.redraft.instructionPlaceholder")}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); void run(); } }}
            />
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {SUGGESTION_KEYS.filter((k) => (k === "translateAr" ? locale === "en" : k === "translateEn" ? locale === "ar" : true)).map((k) => (
                <button key={k} type="button" onClick={() => setInstruction(t(`ai.redraft.suggestions.${k}`))} className="rounded-full border border-sand-200 bg-white px-2.5 py-0.5 text-[11px] text-sand-800 hover:border-seablue-300 hover:bg-seablue-50">{t(`ai.redraft.suggestions.${k}`)}</button>
              ))}
              {text.trim().length === 0 ? <button type="button" onClick={() => setInstruction(t("ai.redraft.suggestions.fromContext"))} className="rounded-full border border-seablue-200 bg-seablue-50 px-2.5 py-0.5 text-[11px] text-seablue-900 hover:bg-seablue-100">{t("ai.redraft.suggestions.fromContext")}</button> : null}
              <Button size="sm" variant="gold" className="ms-auto" disabled={instruction.trim().length < 3 || phase.kind === "streaming"} loading={phase.kind === "streaming"} onClick={() => void run()}>
                <Sparkles /> {phase.kind === "idle" ? t("ai.redraft.run") : t("ai.redraft.rerun")}
              </Button>
            </div>
          </div>

          {phase.kind === "error" ? <InlineAlert tone="danger" title={t("ai.redraft.failed")}>{phase.message}</InlineAlert> : null}
          {done?.offline ? <InlineAlert tone="warning">{t("ai.redraft.offline")}</InlineAlert> : null}
          {done && !done.offline && unchanged ? <InlineAlert tone="info">{t("ai.redraft.unchanged")}</InlineAlert> : null}
          {done && done.flags.some((f) => f === "PromptInjection") ? <InlineAlert tone="warning">{t("ai.redraft.flags")}</InlineAlert> : null}

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
            <Pane title={t("ai.redraft.current")} tone="sand">
              {text.trim() ? <Body text={text} format={format} /> : <p className="text-sm text-muted-foreground">{t("ai.redraft.empty")}</p>}
            </Pane>
            <div className="hidden place-items-center lg:grid"><ArrowRight className="size-4 text-sand-400 rtl:rotate-180" /></div>
            <Pane
              title={t("ai.redraft.proposed")} tone="ai"
              action={done && !unchanged ? <button type="button" onClick={() => setShowChanges((v) => !v)} className="text-[11px] text-seablue-800 underline-offset-2 hover:underline">{showChanges ? t("ai.redraft.hideChanges") : t("ai.redraft.showChanges")}</button> : null}
            >
              {phase.kind === "idle" ? <p className="text-sm text-muted-foreground">{t("ai.redraft.waiting")}</p>
                : segments ? <DiffBody segments={segments} />
                : <Body text={proposed} format={format} streaming={phase.kind === "streaming"} />}
            </Pane>
          </div>
          {done && !done.offline ? <p className="text-[11px] text-muted-foreground">{t("ai.redraft.meter", { ms: done.latencyMs })}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("actions.cancel")}</Button>
          <Button variant="gold" disabled={!canApply} onClick={apply}><Check /> {t("ai.redraft.apply")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Pane({ title, tone, action, children }: { title: string; tone: "sand" | "ai"; action?: ReactNode; children: ReactNode }) {
  return (
    <section className={cn("min-h-40 rounded-xl border p-3", tone === "ai" ? "border-seablue-200 bg-seablue-50/40" : "border-sand-200 bg-sand-50/60")}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className={cn("text-[11px] font-semibold uppercase tracking-wider", tone === "ai" ? "text-seablue-800" : "text-sand-600")}>{title}</p>
        {action}
      </div>
      {children}
    </section>
  );
}

function Body({ text, format, streaming = false }: { text: string; format: RedraftFormat; streaming?: boolean }) {
  if (format === "plain") return <p className="whitespace-pre-wrap text-sm leading-relaxed" dir="auto">{text}{streaming ? <span className="ms-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-seablue-600 align-middle" /> : null}</p>;
  return <MarkdownBody className="prose-sm" streaming={streaming}>{text}</MarkdownBody>;
}

function DiffBody({ segments }: { segments: ReturnType<typeof diffWords> }) {
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed" dir="auto">
      {segments.map((s, i) => s.kind === "same" ? <span key={i}>{s.text}</span> : s.kind === "added" ? <mark key={i} className="rounded bg-success-100 text-success-900 no-underline">{s.text}</mark> : <del key={i} className="rounded bg-danger-100 text-danger-800">{s.text}</del>)}
    </p>
  );
}

// ---------------------------------------------------------------- Button + dialog in one
export interface RedraftActionProps {
  /** Reads the text at the moment the dialog opens, so the redraft always starts from what the officer sees. */
  getText: () => string;
  onApply: (text: string) => void;
  format?: RedraftFormat;
  context?: RedraftContext;
  disabled?: boolean;
  size?: "sm" | "icon-sm";
  variant?: "ghost" | "outline";
  className?: string;
}

/** The "Redraft with AI" control any editor can carry in its toolbar; owns the dialog. */
export function RedraftAction({ getText, onApply, format = "markdown", context, disabled = false, size = "sm", variant = "ghost", className }: RedraftActionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const label = t("ai.redraft.button");
  const openWith = () => { setText(getText()); setOpen(true); };
  return (
    <>
      {size === "icon-sm" ? (
        <Tooltip content={label}><Button size="icon-sm" variant={variant} className={cn("size-7 text-seablue-800 hover:bg-seablue-50 hover:text-seablue-900", className)} aria-label={label} disabled={disabled} onClick={openWith}><WandSparkles className="size-3.5" /></Button></Tooltip>
      ) : (
        <Button size="sm" variant={variant} className={cn("h-7 text-seablue-800 hover:bg-seablue-50 hover:text-seablue-900", className)} disabled={disabled} onClick={openWith}><WandSparkles /> {label}</Button>
      )}
      <RedraftDialog open={open} onOpenChange={setOpen} text={text} format={format} context={context} onApply={onApply} />
    </>
  );
}
